import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EntityHistoryTable } from '../-components/EntityHistoryTable';
import { entityHistoryQueryOptions } from '../-hooks/useEntityHistory';
import { useEntityHistoryPage } from '../-hooks/useEntityHistoryPage';
import { toEntityHistoryQuery } from '../-utils/listParams';

const entityHistorySearch = z.object({
  // Written by `useTable` on every filter change; the feed has no pages.
  page: fallback(z.number().int().min(1), 1),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/entity-history/(pages)/')({
  validateSearch: entityHistorySearch,
  // Read off the URL, so a shared link with filters prefetches the entries it
  // will actually show.
  loaderDeps: ({ search }) => ({
    query: toEntityHistoryQuery(search.filters ?? {}),
  }),
  // Swallowed: the page reports a failed load as a banner above the table.
  loader: ({ deps }) =>
    queryClient
      .ensureInfiniteQueryData(entityHistoryQueryOptions(deps.query))
      .catch(() => undefined),
  component: EntityHistoryPage,
});

function EntityHistoryPage() {
  const page = useEntityHistoryPage();

  return (
    <PageContainer>
      <EntityHistoryTable page={page} />
    </PageContainer>
  );
}
